import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import UpdateIcon from '@mui/icons-material/Update';
import StarIcon from '@mui/icons-material/Star';
import VisibilityIcon from '@mui/icons-material/Visibility';

/**
 * BadgesRow component - Three social proof badges with sentence case
 * Uses factual claims with optional links to relevant explanations
 * Consistent icons and neutral color palette
 */
const BadgesRow = () => {
  const badges = [
    {
      id: 'updated',
      icon: <UpdateIcon data-testid="UpdateIcon" />,
      title: 'Updated for 2025/26',
      subtitle: 'Latest HMRC rates',
      link: '/about-the-salary-calculator',
      color: 'success.main',
      backgroundColor: 'success.50'
    },
    {
      id: 'free',
      icon: <StarIcon data-testid="FreeIcon" />,
      title: '100% free',
      subtitle: 'No sign-up required',
      link: null, // No specific pricing FAQ page mentioned
      color: 'primary.main',
      backgroundColor: 'primary.50'
    },
    {
      id: 'visual',
      icon: <VisibilityIcon data-testid="VisualIcon" />,
      title: 'Visual tax bands',
      subtitle: 'Interactive breakdown',
      link: null, // No specific explainer page mentioned
      color: 'secondary.main',
      backgroundColor: 'secondary.50'
    }
  ];

  const BadgeContent = ({ badge }) => (
    <Box
      sx={{
        textAlign: 'center',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: badge.backgroundColor || 'neutral.100',
          mb: 1,
          mx: 'auto'
        }}
      >
        {React.cloneElement(badge.icon, {
          sx: { 
            color: badge.color,
            fontSize: '1.25rem'
          }
        })}
      </Box>
      
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          color: 'text.primary', // Use text.primary, not colored
          fontSize: { xs: '0.9rem', md: '1rem' },
          mb: 0.5
        }}
      >
        {badge.title}
      </Typography>
      
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: { xs: '0.75rem', md: '0.8rem' }
        }}
      >
        {badge.subtitle}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: { xs: 3, md: 6 },
        flexWrap: 'wrap',
        py: 2
      }}
    >
      {badges.map((badge) => (
        <Box
          key={badge.id}
          role="button"
          tabIndex={0}
          sx={{
            cursor: badge.link ? 'pointer' : 'default',
            '&:focus': badge.link ? {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
              borderRadius: 1
            } : {}
          }}
        >
          {badge.link ? (
            <Box
              component={Link}
              to={badge.link}
              sx={{ textDecoration: 'none' }}
            >
              <BadgeContent badge={badge} />
            </Box>
          ) : (
            <BadgeContent badge={badge} />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default BadgesRow;